import { mapGetters } from 'vuex';
import { ruid } from '@/lib/utils';

/**
 * Mixin for handling of whether the user is idle. Redirects user back to login upon expiration of `idle_logout_timeout`.
 * Events held in data kick off timer unless 'idle_logout_timeout' isn't set, in which case no listeners or timers will be set.
 * Please note: 'mousemove' and 'touchstart' have their issues and are likely overkill anyways, please properly evaluate if ever added.
 */
export const isIdle = {
  data() {
    return {
      timer: Infinity,
      events: ['keydown', 'mousedown'],
    };
  },

  destroyed() {
    this.destroyTimer();
  },

  computed: {
    idleLogoutTimeout() {
      return this.$store.getters['meta/systemParameter']('idle_logout_timeout');
    },

    shouldSaveSiteAndDrawer() {
      return this.$store.getters['meta/systemParameter']('save_site_cashdrawer_on_login') === 'Y';
    },

    ...mapGetters({
      isAuthenticated: 'auth/authenticated',
    }),
  },

  watch: {
    isAuthenticated(newValue, oldValue) {
      if (!this.idleLogoutTimeout || newValue === oldValue) return;
      if (newValue) { // Then logged in, setup timers if system parameter is set.
        this.setupTimer();
        return;
      }
      this.destroyTimer(); //  Otherwise logged out, destroy timer.
    },
  },

  methods: {
    /**
     * Adds event listeners to window and kicks off timer.
     */
    setupTimer() {
      this.events.forEach((event) => {
        window.addEventListener(event, this.resetTimer);
      }, this);
      this.setTimer();
    },

    /**
     * Destroys event listeners. Please test to ensure listeners are destroyed if ever modified.
     */
    destroyTimer() {
      this.events.forEach((event) => {
        window.removeEventListener(event, this.resetTimer);
      }, this);
      clearTimeout(this.timer);
    },

    /**
     * Sets timer based on `idle_logout_timeout` system parameter.
     */
    setTimer() {
      const timeout = this.idleLogoutTimeout * 60000;
      this.timer = setTimeout(this.logout, timeout);
    },

    /**
     * Clears and sets timer.
     */
    resetTimer() {
      this.clearTimer();
      this.setTimer();
    },

    /**
     * Calls clearTimeout to reset timer.
     */
    clearTimer() {
      clearTimeout(this.timer);
    },

    /**
     * Logs out user and redirects back to login via logout action.
     */
    logout() {
      if (!this.isAuthenticated) return;
      const id = ruid();
      const message = {
        id,
        title: 'Logged Out Due to Inactivity',
        text: `You have been inactive for  ${this.idleLogoutTimeout} minutes or more, please log back in to resume.`,
        duration: -1,
      };
      this.$store.dispatch('notify/setLogoutId', id);
      this.$store.dispatch('notify/push', message);
      this.$store.dispatch('auth/logout', { shouldDisassociate: !this.shouldSaveSiteAndDrawer });
    },
  },
};
